// CSS Modules - component class name: FormSelect__

<template>
  <div
    :class="[
      $style.container,
      { FormSelect__blue: this.colour === 'blue' },
      { FormSelect__error: this.error },
      { FormSelect__active: this.isActive }
    ]"
    :aria-label="ariaLabel"
  >
    <label v-if="label" :class="$style.label">
      {{ label }}
      <span :class="$style.optional" v-if="optionalLabel">
        ({{ optionalLabel }})
      </span>
    </label>
    <template v-if="isMultiSelect">
      <Select
        :colour="colour"
        :selectOptions="filteredOptions"
        :selectMultiValue="
          Array.isArray(initialOption) || initialOption !== placeholder
            ? initialOption
            : undefined
        "
        isMulti
        :placeholder="placeholder"
        @multiChanged="handleOptionClick"
        :reset="resetMultiSelects"
        :activeSelect="activeSelect"
        :category="category"
      />
    </template>
    <template v-else>
      <div
        ref="input"
        :class="[$style.input, { FormSelect__disabled: this.disabled }]"
        @click="handleInputClick"
      >
        <span v-html="optionLabel"></span>
        <span :class="$style.triangle"></span>
      </div>
      <div
        ref="optionsContainer"
        v-show="isActive"
        :class="$style.optionsContainer"
        v-closable="{
          exclude: ['input', 'filter'],
          handler: 'handleOutsideClick'
        }"
      >
        <div v-if="hasFilter" ref="filter" :class="$style.filterContainer">
          <input v-model="filter" />
          <i class="fas fa-search"></i>
        </div>
        <ul :class="$style.options" ref="optionsList">
          <li
            v-for="(option, index) in filteredOptions"
            :id="`option${index}`"
            :class="[
              $style.option,
              {
                FormSelect__hover: selectedIndex === index && showSelectedHover
              }
            ]"
            :key="index"
            @click="() => handleOptionClick(option)"
            @mouseover="showSelectedHover = false"
          >
            <!-- This is uglier, but it gets rid of the spaces between the spans... -->
            <span v-html="option.label ? option.label[0] : option[0]"></span>
            <span
              :class="$style.highlighted"
              v-html="option.label ? option.label[1] : option[1]"
            ></span>
            <span v-html="option.label ? option.label[2] : option[2]"></span>
          </li>
        </ul>
      </div>
    </template>
    <div v-if="error" :class="$style.errorMessage">{{ errorMessage }}</div>
  </div>
</template>

<script>
import closable from "../../directives/Closable.directive";
import Select from "../Select/Select.vue";

export default {
  name: "FormSelect",
  components: {
    Select
  },
  props: {
    value: [String, Number, Object, Array],
    label: {
      type: String,
      default: ""
    },
    errorMessage: {
      type: String,
      default: ""
    },
    error: {
      type: Boolean,
      default: false
    },
    options: {
      type: Array,
      default: () => []
    },
    initialOption: {
      type: [String, Number, Object, Array],
      default: ""
    },
    disabled: {
      type: Boolean,
      default: false
    },
    hasFilter: {
      type: Boolean,
      default: false
    },
    colour: {
      type: String,
      default: ""
    },
    optionalLabel: String,
    ariaLabel: {
      type: String,
      default: undefined
    },
    isMultiSelect: {
      type: Boolean,
      default: false
    },
    resetMultiSelects: {
      type: Boolean,
      default: false
    },
    category: {
      type: String,
      default: undefined
    },
    activeSelect: {
      type: String,
      default: undefined
    },
    placeholder: {
      type: String,
      default: undefined
    }
  },
  data() {
    return {
      isActive: false,
      filter: "",
      selectedIndex: -1,
      showSelectedHover: false
    };
  },
  directives: {
    closable
  },
  computed: {
    optionLabel: function() {
      return this.value.label ? [...this.value.label].join("") : this.value;
    },
    optionValue: function() {
      return this.value.value ? this.value.value : this.value;
    },
    filteredOptions: function() {
      const filteredOptions = this.options.filter(option => {
        return (
          this.filter === "" ||
          (option.label
            ? option.label.toLowerCase().indexOf(this.filter.toLowerCase()) !==
              -1
            : option.toLowerCase().indexOf(this.filter.toLowerCase()) !== -1)
        );
      });

      const dataOptions = filteredOptions.map(option => {
        const optionText = option.label ? option.label : option;
        const optionLength = optionText.length;
        const filterLength = this.filter.length;
        const startPosition = optionText
          .toLowerCase()
          .indexOf(this.filter.toLowerCase());
        if (this.filter === "") {
          if (option.label) {
            return {
              value: option.value,
              label: [option.label, "", ""]
            };
          }
          return [option, "", ""];
        } else if (startPosition === 0 && filterLength < optionLength) {
          if (option.label) {
            return {
              value: option.value,
              label: [option.label, "", ""]
            };
          }
          return [
            "",
            option.substr(startPosition, filterLength),
            option.substring(filterLength, optionLength)
          ];
        } else if (startPosition === 0 && filterLength === optionLength) {
          if (option.label) {
            return {
              value: option.value,
              label: ["", option.label, ""]
            };
          }
          return ["", option, ""];
        } else if (
          startPosition > 0 &&
          filterLength + startPosition < optionLength
        ) {
          if (option.label) {
            return {
              value: option.value,
              label: [
                option.label.substring(0, startPosition),
                option.label.substr(startPosition, filterLength),
                option.label.substring(
                  filterLength + startPosition,
                  optionLength
                )
              ]
            };
          }
          return [
            option.substring(0, startPosition),
            option.substr(startPosition, filterLength),
            option.substring(filterLength + startPosition, optionLength)
          ];
        } else {
          if (option.label) {
            return {
              value: option.value,
              label: [
                option.label.substring(0, startPosition),
                option.label.substring(startPosition, optionLength),
                ""
              ]
            };
          }
          return [
            option.substring(0, startPosition),
            option.substring(startPosition, optionLength),
            ""
          ];
        }
      });
      if (this.isMultiSelect) {
        let multiDataOptions = [];
        for (let option of dataOptions) {
          multiDataOptions.push(option[0]);
        }
        return multiDataOptions;
      } else {
        return dataOptions;
      }
    }
  },
  mounted() {
    if (this.initialOption) {
      if (
        !this.isMultiSelect ||
        (this.isMultiSelect && !Array.isArray(this.initialOption))
      ) {
        this.options.filter((option, index) => {
          if (this.initialOption === (option.value ? option.value : option)) {
            this.selectedIndex = index;
            return null;
          }
        });
      } else {
        let preselectedOptions = [];
        for (let option of this.options) {
          for (let initOption of this.initialOption) {
            if (initOption === option) {
              preselectedOptions.push(option);
            }
          }
        }
        if (preselectedOptions !== []) {
          this.$emit("input", preselectedOptions);
        }
      }
    }
  },
  methods: {
    handleInputClick() {
      if (!this.disabled) {
        this.isActive = !this.isActive;
        if (this.isActive && this.selectedIndex > -1) {
          setTimeout(() => {
            this.showSelectedHover = true;
            if (this.selectedIndex > 6) {
              const list = this.$refs.optionsList;
              const scrollTo = (this.selectedIndex - 6) * 30;
              list.scrollTo(0, scrollTo);
            }
          }, 0);
        } else if (!this.isActive) {
          this.showSelectedHover = false;
          this.filter = "";
        }
      }
    },
    handleOutsideClick() {
      this.isActive = false;
      this.filter = "";
    },
    handleOptionClick(option) {
      if (this.isMultiSelect) {
        if (option.length > 1) {
          this.$emit("input", option);
        } else {
          this.$emit("input", option.join(""));
        }
      } else {
        this.$emit("input", option.value ? option : option.join(""));
      }
      this.options.filter((propOption, index) => {
        if (
          option.value
            ? option.value === propOption.value
            : option.join("") === propOption
        ) {
          this.selectedIndex = index;
        }
      });
      this.isActive = false;
      this.filter = "";
    }
  }
};
</script>

<style module lang="scss">
@import "./FormSelect.scss";
</style>
